import React from "react";

import { useContainerDimensions } from "@/hooks/use-container-dimensions";
/**
 * ChatAvatarPresentationAnimation Component
 *
 * Animates between two layouts:
 * - Before: Two avatars side by side
 * - After: Two avatars stacked vertically on the left, with a presentation and footer on the right
 *
 * Mathematical Constraints (for the "after" state):
 * 1. containerWidth = afterLeftWidth + gap + afterRightWidth
 * 2. afterRightWidth / presentationHeight = 16 / 9 (presentation aspect ratio)
 * 3. rightColumnHeight = presentationHeight + presentationFooterHeight
 * 4. afterRightWidth / afterLeftWidth = widthRatio (customizable, default: 8/2)
 * 5. leftColumnHeight = (containerHeight - gap) / 2 + gap
 * 6. leftColumnHeight = rightColumnHeight
 *
 * Before State Layout:
 * ┌─────────────────────────────────────────────────────┐
 * │                   containerWidth                    │
 * ├────────────────────┬─────┬──────────────────────────┤
 * │                    │ gap │                          │
 * │    userAvatar      │     │      botAvatar           │
 * │  (beforeLeftWidth) │     │  (beforeRightWidth)      │
 * │                    │     │                          │
 * │                    │     │                          │
 * └────────────────────┴─────┴──────────────────────────┘
 *         ↑                              ↑
 *    (containerWidth - gap) / 2    (containerWidth - gap) / 2
 *
 *
 * After State Layout:
 * ┌─────────────────────────────────────────────────────────────┐
 * │                        containerWidth                       │
 * ├──────────────────┬─────┬────────────────────────────────────┤
 * │                  │     │                                    │
 * │                  │     │        presentationHeader          │
 * │    userAvatar    │     │     (presentationHeaderHeight)     │
 * │ (afterRowHeight) │ gap ├────────────────────────────────────┤
 * │                  │     │                                    │
 * ├──────────────────┤     │          presentation              │
 * │       gap        │     │           (16:9 ratio)             │
 * ├──────────────────┤     │       (presentationHeight)         │
 * │                  │     ├────────────────────────────────────┤
 * │                  │     │                                    │
 * │    botAvatar     │     │        presentationFooter          │
 * │ (afterRowHeight) │     │     (presentationFooterHeight)     │
 * │                  │     │                                    │
 * └──────────────────┴─────┴────────────────────────────────────┘
 *         ↑                              ↑
 *   afterLeftWidth               afterRightWidth
 *
 */
export const ChatAvatarPresentationAnimation = ({
  /** Gap between columns and rows. */
  gap = 10,
  /** This is the ratio of the right column (presentation) over the left column (avatars) */
  widthRatio = 8 / 2,
  /**
   * This allows extra presentation view such as PDF page controller to be rendered below
   * the presentation without affecting the presentation's aspect ratio.
   */
  presentationFooterHeight = 0,
  /**
   * This is similar to `presentationFooterHeight`, but for header instead.
   */
  presentationHeaderHeight = 0,
  userAvatar = <div className="bg-black h-full" />,
  botAvatar = <div className="bg-blue-700 h-full" />,
  presentation = <div className="bg-red-800 h-full" />,
  showPresentation = false,
  /** In milliseconds. */
  transitionDuration = 500,
  presentationAspectRatio = MODERN_SLIDE_ASPECT_RATIO,
}) => {
  const [animateAvatars, setAnimateAvatars] = React.useState(false);
  const [animatePresentation, setAnimatePresentation] = React.useState(false);
  const [isInitialRender, setIsInitialRender] = React.useState(true);

  React.useEffect(() => {
    // This is necessary to prevent the UserAvatar from
    // sliding from the left, rightward into its position
    // during the initial render
    setTimeout(() => {
      setIsInitialRender(false);
    }, 500);
  }, []);

  const container = useContainerDimensions();

  React.useEffect(() => {
    if (showPresentation) {
      // Enter animation: avatars first, then presentation
      setAnimateAvatars(true);
      const timer = setTimeout(() => {
        setAnimatePresentation(true);
      }, transitionDuration / 1.6);
      return () => clearTimeout(timer);
    } else {
      // Exit animation: presentation first, then avatars
      setAnimatePresentation(false);
      const timer = setTimeout(() => {
        setAnimateAvatars(false);
      }, transitionDuration / 1.6);
      return () => clearTimeout(timer);
    }
  }, [showPresentation]);

  // Calculate dimensions
  // Before state: simple split

  /** This is set by Jing Yi to be 5/4. */
  const avatarAspectRatio = 5 / 4;
  const containerHeight = container.dimensions.width / avatarAspectRatio;
  const beforeLeftWidth = (container.dimensions.width - gap) / 2;
  const beforeRightWidth = (container.dimensions.width - gap) / 2;
  const beforeRowHeight = (containerHeight - gap) / 2; // Use provided height for before state

  // After state: using customizable width ratio
  const afterLeftWidth = (container.dimensions.width - gap) / (1 + widthRatio);
  const afterRightWidth = widthRatio * afterLeftWidth;

  // Calculate height based on the aspect ratio of the presentation
  const presentationHeight = afterRightWidth / presentationAspectRatio;

  // Apply constraint 3: rightColumnHeight = presentationHeight + presentationHeaderHeight + presentationFooterHeight
  const rightColumnHeight =
    presentationHeight + presentationHeaderHeight + presentationFooterHeight;

  // Apply constraint 6: leftColumnHeight = rightColumnHeight
  const leftColumnHeight = rightColumnHeight;

  // From constraint 5: leftColumnHeight = (containerHeight - gap) / 2 + gap
  // We can derive: afterRowHeight = (leftColumnHeight - gap) / 2
  const afterRowHeight = (leftColumnHeight - gap) / 2;

  // The actual container height in the "after" state
  const calculatedContainerHeight = rightColumnHeight;

  return (
    <div
      ref={container.setContainerRef}
      style={{
        position: "relative",
        width: "100%",
        height: animateAvatars
          ? `${calculatedContainerHeight}px`
          : showPresentation
            ? `${containerHeight}px`
            : beforeRowHeight,
        transition: `height ${transitionDuration}ms ease`,
      }}
    >
      {/* Bot Avatar */}
      <div
        style={{
          position: "absolute",
          display: "grid",
          top: "0px",
          left: "0px",
          width: animateAvatars
            ? `${afterLeftWidth}px`
            : `${beforeLeftWidth}px`,
          height: animateAvatars
            ? `${afterRowHeight}px`
            : `${beforeRowHeight}px`,
          transition: `width ${transitionDuration}ms ease, height ${transitionDuration}ms ease`,
        }}
      >
        {botAvatar}
      </div>

      {/* User Avatar */}
      <div
        style={{
          position: "absolute",
          display: "grid",
          top: "0px",
          left: "0px",
          width: animateAvatars
            ? `${afterLeftWidth}px`
            : `${beforeRightWidth}px`,
          height: animateAvatars
            ? `${afterRowHeight}px`
            : `${beforeRowHeight}px`,
          transition: isInitialRender
            ? undefined
            : `transform ${transitionDuration}ms ease, width ${transitionDuration}ms ease, height ${transitionDuration}ms ease`,
          transform: animateAvatars
            ? `translateY(${afterRowHeight + gap}px)`
            : `translateX(${beforeLeftWidth + gap}px)`,
        }}
      >
        {userAvatar}
      </div>

      {/* Presentation */}
      <div
        style={{
          position: "absolute",
          overflow: "hidden",
          display: "grid",
          top: "0px",
          left: `${afterLeftWidth + gap}px`,
          width: `${afterRightWidth}px`,
          height: `${rightColumnHeight}px`,
          transition: `opacity ${transitionDuration}ms ease, transform ${transitionDuration}ms ease, height ${transitionDuration}ms ease`,
          opacity: animatePresentation ? 1 : 0,
          transform: animatePresentation ? "scale(1)" : "scale(0)",
          transformOrigin: "center",
        }}
      >
        <div className="grid h-full w-full overflow-hidden bg-black rounded-sm">
          {presentation}
        </div>
      </div>
    </div>
  );
};

/** See https://arc.net/l/quote/duysmaao */
export const MODERN_SLIDE_ASPECT_RATIO = 16 / 9;
